import { useState, useEffect, useCallback, useRef } from 'react';
import { getRivhitSyncStatus } from '../api/client';

export interface RivhitSyncState {
  /** Whether a sync is currently in progress */
  syncing: boolean;
  /** Last sync finished timestamp (ISO string) or null */
  lastSyncAt: string | null;
  /** Error message if sync failed */
  error: string | null;
}

const POLL_INTERVAL_MS = 3000;
/**
 * Hard cap on how long we keep polling the sync-status endpoint. If the backend
 * status stays `in_progress`/`unknown` indefinitely (e.g. a stuck or lost sync
 * job), the interval would otherwise poll forever — silently driving a constant
 * stream of API/Lambda calls for every open browser tab. After this deadline we
 * stop polling and surface a timeout error.
 */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

export interface RivhitSyncStatusOptions {
  /** When false the hook skips all polling (use when Rivhit is not the active provider). Defaults to true. */
  enabled?: boolean;
}

/**
 * Hook that polls the Rivhit sync-status endpoint and exposes current state.
 * When a sync completes, it calls `onSyncComplete` so the page can refresh.
 * Pass `enabled: false` when Rivhit is not the active invoice provider to avoid
 * spurious polling.
 */
export function useRivhitSyncStatus(
  onSyncComplete?: () => void,
  { enabled = true }: RivhitSyncStatusOptions = {},
): RivhitSyncState {
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadlineRef = useRef<number>(0);
  const onSyncCompleteRef = useRef(onSyncComplete);
  onSyncCompleteRef.current = onSyncComplete;

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    setSyncing(true);
    setError(null);
    deadlineRef.current = Date.now() + MAX_POLL_DURATION_MS;
    pollingRef.current = setInterval(async () => {
      if (Date.now() > deadlineRef.current) {
        setSyncing(false);
        setError('Sync status check timed out');
        stopPolling();
        return;
      }
      try {
        const res = await getRivhitSyncStatus();
        const status = res.data?.status;
        if (status === 'complete') {
          setSyncing(false);
          setLastSyncAt(res.data?.finished_at ?? new Date().toISOString());
          stopPolling();
          onSyncCompleteRef.current?.();
        } else if (status === 'error') {
          setSyncing(false);
          setError(res.data?.message || 'Sync failed');
          stopPolling();
        }
      } catch {
        // transient error — keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // On mount (or when enabled changes), check if a sync is already in progress
  useEffect(() => {
    if (!enabled) return;
    getRivhitSyncStatus()
      .then((res) => {
        const status = res.data?.status;
        if (status === 'in_progress') {
          startPolling();
        } else if (status === 'complete') {
          setLastSyncAt(res.data?.finished_at ?? null);
        } else if (status === 'error') {
          setError(res.data?.message || 'Last sync failed');
        }
      })
      .catch(() => { /* ignore */ });
    return () => stopPolling();
  }, [enabled, startPolling, stopPolling]);

  return { syncing, lastSyncAt, error };
}
